<div class="schema-page">
  <div class="schema-header">
    <h1>Country schema · XML submission template</h1>
    <p>The XML envelope and per-event fragments used to render this country's submission.</p>
  </div>

  <app-error-banner [message]="errorMessage()" />

  @if (isLoading()) {
    <p class="hint">Loading...</p>
  } @else if (availableCountries().length === 0) {
    <app-empty-state message="No country has a configured schema yet." />
  } @else {
    <div class="toolbar">
      <div class="country-toggle">
        @for (code of availableCountries(); track code) {
          <button
            class="toggle-btn"
            [class.toggle-btn--active]="selectedCountry() === code"
            (click)="selectCountry(code)"
          >
            {{ countryLabel(code) }}
          </button>
        }
      </div>
    </div>

    <div class="xml-section">
      <app-error-banner [message]="templatesError()" />

      @if (isLoadingTemplates()) {
        <p class="hint">Loading...</p>
      } @else {
        <section class="template-card">
          <div class="template-card__header">
            <div>
              <h2>Document envelope</h2>
              <p class="template-hint">
                The root element for {{ selectedCountry() }}'s submission. Must contain a {{ '{{events}}' }} slot —
                the rendered events are spliced in there.
              </p>
            </div>
            @if (canEditTemplates) {
              <button mat-stroked-button class="btn-edit" (click)="openTemplateDialog(documentTemplate)">
                <mat-icon>{{ documentTemplate ? 'edit' : 'add' }}</mat-icon>
                {{ documentTemplate ? 'Edit' : 'Add' }}
              </button>
            }
          </div>

          @if (documentTemplate; as doc) {
            <pre class="template-body">{{ doc.template }}</pre>
            @if (doc.referencedTerms.length > 0) {
              <div class="term-pills">
                @for (term of doc.referencedTerms; track term) {
                  <code class="term-pill">{{ term }}</code>
                }
              </div>
            }
            @if (canEditTemplates) {
              <button class="btn-remove" (click)="deleteTemplate(doc)">
                <span class="material-icons">delete_outline</span>
                Remove
              </button>
            }
          } @else {
            <app-empty-state
              [message]="'No document envelope set for ' + selectedCountry() + ' yet — previews for it will fail until one is added.'"
            />
          }
        </section>

        @if (documentTemplate) {
          <section class="template-card">
            <div class="template-card__header">
              <div>
                <h2>Event fragments</h2>
                <p class="template-hint">
                  One per EPCIS event type, with {{ '*' }} as the fallback every other type renders through.
                </p>
              </div>
              @if (canEditTemplates) {
                <button mat-stroked-button class="btn-edit" (click)="openTemplateDialog(undefined, 'EVENT')">
                  <mat-icon>add</mat-icon>
                  Add fragment
                </button>
              }
            </div>

            @if (eventTemplates.length === 0) {
              <app-empty-state
                [message]="'No event fragment for ' + selectedCountry() + ' — a selection will fail to render until at least the wildcard (*) exists.'"
              />
            } @else {
              @for (tpl of eventTemplates; track tpl.eventType) {
                <div class="fragment-row">
                  <div class="fragment-row__header">
                    <h3>{{ eventTemplateLabel(tpl.eventType) }}</h3>
                    @if (canEditTemplates) {
                      <div class="fragment-row__actions">
                        <button class="btn-icon" title="Edit" (click)="openTemplateDialog(tpl)">
                          <span class="material-icons">edit</span>
                        </button>
                        <button class="btn-icon btn-icon--danger" title="Remove" (click)="deleteTemplate(tpl)">
                          <span class="material-icons">delete_outline</span>
                        </button>
                      </div>
                    }
                  </div>
                  <pre class="template-body">{{ tpl.template }}</pre>
                  @if (tpl.referencedTerms.length > 0) {
                    <div class="term-pills">
                      @for (term of tpl.referencedTerms; track term) {
                        <code class="term-pill">{{ term }}</code>
                      }
                    </div>
                  }
                </div>
              }
            }
          </section>
        }
      }
    </div>
  }
</div>
